import React from 'react';
import Banner from '../../../composants/Banner/Banner';
import { OperationMS } from '../types';
import { CommandeAnalyse } from '../domain/analyse/types';
import { buildFinancialPool, resolveNoteFinanciere } from '../domain/analyse/scoring';
import { getMontantAffiche } from '../domain/analyse/montants';
import { formatNoteGlobale } from '../domain/analyse/notationTechnique';
import { isOffreMasquee, isExpire, isRefuse, isPending, isAccepte } from '../domain/analyse/statutOffre';
import { getCandidatureDate } from '../domain/analyse/historique';
import { formatDateHeure } from '../domain/analyse/dates';

// Grille financiere : DPGF par offre (sections / articles / prix), total HT et note financiere.
// Montant et note viennent du domaine ; aucun fetch propre (les commandes sont deja chargees).
interface OperationAnalyseFinanciereProps {
  operation: OperationMS;
}

const euro = (value: number): string =>
  value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Rappel de la formule de notation financiere en vigueur dans Centralis (reference, texte fige).
const FORMULE_MEMO =
  'Pour mémoire, la formule utilisée dans Centralis est la suivante : sur 10 points = ' +
  "(prix le plus bas / prix de l'offre examinée) x 10. Les prix sont analysés en HT.";

const OperationAnalyseFinanciere: React.FC<OperationAnalyseFinanciereProps> = ({ operation }) => {
  const commandes = (operation.commandes ?? []) as CommandeAnalyse[];
  const pool = buildFinancialPool(commandes);

  return (
    <>
      <Banner variant="info" message={FORMULE_MEMO} />

      {commandes.length === 0 ? (
        <p className="opms__empty">Aucune offre financière à afficher pour cette opération.</p>
      ) : (
        <div className="opms__presta-grid">
          {commandes.map((commande, idx) => {
        const idStatus = commande.id_status;
        const hidden = isOffreMasquee(idStatus);
        const nom = commande.contrat?.entite?.nom || `Prestataire ${idx + 1}`;
        const candidatureDate = formatDateHeure(getCandidatureDate(operation.historique, commande.id));
        const note = resolveNoteFinanciere(commande, pool);
        const total = getMontantAffiche(commande);

        return (
          <div key={String(commande.id)} className={`opms__presta-col${hidden ? ' opms__presta-col--disabled' : ''}`}>
            {isExpire(idStatus) && <div className="opms__presta-badge">Expiré</div>}
            {isRefuse(idStatus) && <div className="opms__presta-badge">Refusé</div>}
            {isPending(idStatus) && <div className="opms__presta-badge opms__presta-badge--ok">Signature en cours</div>}
            {isAccepte(idStatus) && <div className="opms__presta-badge opms__presta-badge--ok">Accepté</div>}

            <div className="opms__presta-header">
              <h3>{nom}</h3>
              <span>{hidden ? '-' : candidatureDate ? `Offre reçue le ${candidatureDate}` : 'Offre reçue'}</span>
            </div>

            <table className="opms__articles-table">
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Prix HT</th>
                </tr>
              </thead>
              <tbody>
                {(commande.sections || []).map((section, sIdx) => (
                  <React.Fragment key={section.id ?? sIdx}>
                    <tr>
                      <td colSpan={2} className="opms__articles-section">{section.name || section.nom || ''}</td>
                    </tr>
                    {(section.articles || []).map((article, aIdx) => (
                      <tr key={article.id ?? aIdx}>
                        <td>
                          <div className="opms__article-numero">
                            <span className="opms__article-code">{article.code ?? ''}</span>
                            {article.nom && (
                              <span className="opms__article-name" aria-label={article.nom}>
                                {article.nom.length > 56
                                  ? `${article.nom.slice(0, 56).trimEnd()}…`
                                  : article.nom}
                                <span className="opms__tooltip">{article.nom}</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{hidden ? '-' : `${euro(Number(article.prix) || 0)} €`}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <div className="opms__total-row">
              Total DPGF HT :&nbsp;{hidden ? '-' : `${euro(total)} €`}
            </div>

            <div className="opms__note-globale">
              <h4>Note financière</h4>
              <div className="opms__note-value">{hidden ? '-' : `${formatNoteGlobale(note)} / 10`}</div>
            </div>
          </div>
        );
          })}
        </div>
      )}
    </>
  );
};

export default OperationAnalyseFinanciere;
